import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, Send, X, Bot, Sparkles } from "lucide-react";

const getAIResponse = (input) => {
  const query = input.toLowerCase().trim();

  if (query.includes("ceo") || query.includes("founder") || query.includes("alishba")) {
    return "Atronm's CEO and Founder is Alishba Zahid. She is a specialized AI & Machine Learning engineer leading our technology vision and business strategy.";
  }
  if (query.includes("moiz") || query.includes("manager") || query.includes("co-founder")) {
    return "Muhammad Moiz Ahmad is our Co-founder and Manager. He specializes in full-stack engineering, project management, and client coordination.";
  }
  if (query.includes("cto") || query.includes("arham")) {
    return "Muhammad Arham is our Chief Technology Officer (CTO). He manages system architecture, custom software development, and technical quality.";
  }
  if (query.includes("cfo") || query.includes("mohsin") || query.includes("aujla")) {
    return "Mohsin Raza Aujla is our Chief Financial Officer (CFO). He handles financial analytics, budgeting, and fiscal partnerships.";
  }
  if (query.includes("what is in") || query.includes("included") || query.includes("contain") || query.includes("comprise")) {
    return "In our services, you receive comprehensive, custom-built solutions: responsive UI frontends, robust API architectures, automated AI agents, database ledgers, end-to-end security audits, and continuous performance tuning.";
  }
  if (query.includes("how we offer") || query.includes("how do you offer") || query.includes("method") || query.includes("deliver")) {
    return "We offer our services through a clear 3-stage process: 1. Discovery & Strategy (milestone mapping), 2. Bespoke Engineering (high-performance React/Node coding), and 3. Edge Deployment (launching on cloud systems with automated scaling).";
  }
  if (query.includes("service") || query.includes("what are your services") || query.includes("web") || query.includes("app") || query.includes("ai") || query.includes("cloud") || query.includes("devops")) {
    return "Atronm offers 5 core digital services: AI & Machine Learning, Web Development, Cross-platform Mobile Apps, Cloud & DevOps, and Custom Enterprise ERP/POS Software.";
  }
  if (query.includes("project") || query.includes("deal") || query.includes("what can you do") || query.includes("do you build")) {
    return "We develop custom software projects including: 1. Enterprise ERP & POS Systems (credit ledgers, fuel tank dip levels), 2. Advanced AI/ML platforms (medical billing RCM agents), 3. High-performance web apps (React, Node), and 4. Secure DevOps infrastructures.";
  }
  if (query.includes("contact") || query.includes("email") || query.includes("phone") || query.includes("hire") || query.includes("location")) {
    return "You can contact Atronm directly through our Contact page (/contact) or by emailing us at info@atronm.com. We are based in Lahore, Pakistan and work with clients globally.";
  }
  if (query.includes("process") || query.includes("workflow") || query.includes("milestone")) {
    return "Our development process is completely transparent. We provide bi-weekly updates, deploy staging links for client testing, and perform rigorous quality assurance before delivery.";
  }
  if (query.includes("hello") || query.includes("hi") || query.includes("hey") || query.includes("greet")) {
    return "Hello! How can I help you today? Ask me about our services, methodologies, or how to contact our team!";
  }
  if (query.includes("thank") || query.includes("thanks") || query.includes("cool")) {
    return "You are very welcome! Let me know if you need anything else.";
  }

  return "I'm sorry, I didn't quite catch that. Could you ask about our Services, what projects we do, how we offer them, or how to contact us?";
};

export default function AIChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi! I am the Atronm AI assistant. How can I help you today? Ask me about our services, our methodology, or how to contact our team!",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = (textToSend) => {
    const text = textToSend || inputValue;
    if (!text.trim()) return;

    // Append user message
    setMessages((prev) => [...prev, { sender: "user", text }]);
    if (!textToSend) setInputValue("");

    // Trigger typing simulation
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = getAIResponse(text);
      setIsTyping(false);
      setMessages((prev) => [...prev, { sender: "bot", text: botResponse }]);
    }, 850);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const suggestions = [
    "What are your services?",
    "What projects do you do?",
    "How do you offer them?",
    "How to contact you?",
  ];

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      {/* Chat Toggle Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(99,102,241,0.4)] hover:scale-110 active:scale-95 transition-all duration-300 relative group"
          aria-label="Open chat"
        >
          <MessageSquare className="w-6 h-6 group-hover:rotate-6 transition-transform" />
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-white"></span>
          </span>
          {/* Tooltip */}
          <span className="absolute right-16 bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-xl shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none border border-slate-800">
            Let's Chat AI
          </span>
        </button>
      )}

      {/* Chat Window Container */}
      {isOpen && (
        <div
          className="w-[330px] sm:w-[380px] h-[500px] bg-slate-950/95 border border-slate-800 rounded-3xl shadow-2xl flex flex-col overflow-hidden backdrop-blur-xl animate-in slide-in-from-bottom-5 duration-200"
          data-aos="fade-up"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-900/60 to-blue-900/60 p-4 border-b border-slate-800 flex justify-between items-center">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-slate-800 border border-slate-700 rounded-2xl flex items-center justify-center text-blue-400">
                <Bot className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-white flex items-center gap-1.5">
                  Atronm AI Assistant
                  <Sparkles className="w-3.5 h-3.5 text-purple-400 animate-pulse" />
                </span>
                <span className="text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                  Online
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white hover:bg-slate-800/80 p-1.5 rounded-full transition-colors focus:outline-none"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages List Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide bg-slate-950/40">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-xs sm:text-sm leading-relaxed shadow-sm ${
                    msg.sender === "user"
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-none"
                      : "bg-slate-850 border border-slate-800 text-slate-200 rounded-bl-none"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Simulated Typing Anim */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-850 border border-slate-800 text-slate-400 rounded-2xl rounded-bl-none px-4 py-3 flex items-center gap-1 shadow-sm">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            )}
            <div ref={scrollToBottom} />
          </div>

          {/* Suggestion Chips */}
          <div className="px-4 py-2 flex flex-wrap gap-1.5 border-t border-slate-900/60 bg-slate-950/20">
            {suggestions.map((sug, idx) => (
              <button
                key={idx}
                onClick={() => handleSend(sug)}
                className="text-[10px] sm:text-xs text-slate-400 hover:text-white bg-slate-900/80 hover:bg-slate-800 border border-slate-800 rounded-xl px-2.5 py-1 transition-all focus:outline-none"
              >
                {sug}
              </button>
            ))}
          </div>

          {/* Input Control Box */}
          <div className="p-3 border-t border-slate-800 bg-slate-950 flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask about Atronm..."
              className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-600 transition-colors"
            />
            <button
              onClick={() => handleSend()}
              className="bg-purple-600 hover:bg-purple-700 text-white p-2.5 rounded-xl flex items-center justify-center transition-colors focus:outline-none"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
